import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import { useNavigate } from 'react-router-dom'
import { Box, Icon, Typography } from '@mui/material'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'

import { format } from 'date-fns'
import TerminateEmployeeForm from './TerminateEmployeeForm'
import TerminationReplacementForm from './TerminationReplacementForm'
import { useMutation, useQueryClient } from 'react-query'
import {
  getTerminationImpact,
  terminateEmployee,
} from 'services/api/modules/staff-management/staffManagementDashboard'
import Bugsnag from '@bugsnag/js'
import AdxButton from 'components/custom/AdxButton'

const emptyReplacements = {
  reportingManager: null,
  departmentHeads: {},
  approvalRoles: {},
}

const requiresReplacements = (impact) =>
  (impact?.direct_reports?.length || 0) > 0 ||
  (impact?.departments_headed?.length || 0) > 0 ||
  (impact?.sole_approval_groups?.length || 0) > 0

const TerminateStaff = ({ staff, employeeID }) => {
  const navigate = useNavigate()
  const [pageState, setPageState] = useState('FILL_DETAILS')
  const [theSchema, setSchema] = useState()
  const [valueSubmission, setValueSubmission] = useState({})
  const [impact, setImpact] = useState(null)
  const [replacements, setReplacements] = useState(emptyReplacements)
  const [replacementErrors, setReplacementErrors] = useState({})
  const [requestError, setRequestError] = useState(null)
  const [terminationResult, setTerminationResult] = useState(null)

  const queryClient = useQueryClient()

  const replacementStaff = impact?.replacement_candidates || []

  const impactMutation = useMutation(
    (offboardingDate) => getTerminationImpact(employeeID, offboardingDate),
    {
      onSuccess: (response) => {
        const impactData = response?.data
        setImpact(impactData)
        setReplacements(emptyReplacements)
        setReplacementErrors({})
        setPageState(requiresReplacements(impactData) ? 'REPLACEMENTS' : 'CONFIRMATION_STAGE')
      },
      onError: (error) => {
        setRequestError(error?.data?.message || 'Unable to check the termination impact.')
      },
    }
  )

  const terminationMutation = useMutation((value) => terminateEmployee(employeeID, value), {
    onSuccess: (response) => {
      queryClient.invalidateQueries('detailedEmployeeData')
      queryClient.invalidateQueries('employeePersonalData')
      queryClient.invalidateQueries('staffListData')
      setTerminationResult(response?.data?.data || null)
      setRequestError(null)
      setPageState('SUCCESSFUL_REQUEST')
    },
    onError: (error) => {
      setRequestError(error?.data?.message || 'Unable to terminate this staff member.')
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const getValidationSchema = (schema) => {
    setSchema(schema)
  }

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    navigate(-1)
  }

  const handleSubmission = () => {
    const payload = { offboarding_date: format(valueSubmission, 'yyyy-MM-dd') }

    if (requiresReplacements(impact)) {
      payload.replacements = {
        reporting_manager_id: replacements.reportingManager?.id || null,
        department_heads: Object.fromEntries(
          Object.entries(replacements.departmentHeads).map(([departmentId, replacement]) => [
            departmentId,
            replacement.id,
          ])
        ),
        approval_roles: Object.fromEntries(
          Object.entries(replacements.approvalRoles).map(([roleId, replacement]) => [
            roleId,
            replacement.id,
          ])
        ),
      }
    }

    setRequestError(null)
    terminationMutation.mutate(payload)
  }

  const handleDateSubmission = (values) => {
    setValueSubmission(values.date)
    setRequestError(null)
    impactMutation.mutate(format(values.date, 'yyyy-MM-dd'))
  }

  const handleReplacementSubmission = () => {
    const nextErrors = { departmentHeads: {}, approvalRoles: {} }

    if ((impact?.direct_reports?.length || 0) > 0 && !replacements.reportingManager) {
      nextErrors.reportingManager = 'Please select a new reporting manager.'
    }

    const departments = impact?.departments_headed || []
    departments.forEach((department) => {
      if (!replacements.departmentHeads[department.id]) {
        nextErrors.departmentHeads[department.id] = `Please select a new head for ${department.name}.`
      }
    })

    const approvalRoles = impact?.sole_approval_groups || []
    approvalRoles.forEach((role) => {
      if (!replacements.approvalRoles[role.id]) {
        nextErrors.approvalRoles[role.id] = `Please select a replacement for ${role.name}.`
      }
    })

    setReplacementErrors(nextErrors)
    const hasErrors =
      Boolean(nextErrors.reportingManager) ||
      Object.keys(nextErrors.departmentHeads).length > 0 ||
      Object.keys(nextErrors.approvalRoles).length > 0

    if (!hasErrors) {
      setRequestError(null)
      setPageState('CONFIRMATION_STAGE')
    }
  }

  if (pageState === 'SUCCESSFUL_REQUEST') {
    const finalized = terminationResult?.finalized

    return (
      <AdxInfoModal
        variant="error"
        open={true}
        muiIcon="person_off"
        iconColor="error.main"
        title={finalized ? 'Staff Terminated' : 'Staff Termination Scheduled'}
        subtitle={
          finalized
            ? `${staff?.name} has been terminated.`
            : `${format(valueSubmission, 'dd/MM/yyyy')} is ${
                staff?.name
              }'s last working day. Termination will be finalized after this date.`
        }
        footerButtons
        firstBtnLabel="Close"
        firstBtnHandle={() => navigate(-1)}
      />
    )
  }

  if (pageState === 'CONFIRMATION_STAGE') {
    return (
      <AdxInfoModal
        variant="error"
        open={true}
        muiIcon="error"
        iconColor="error.main"
        title="Terminate Staff?"
        subtitle={`You’re going to terminate ${staff?.name}. The selected date remains their last working day, and termination is finalized afterward.`}
        footerButtons
        firstBtnLabel="Proceed"
        firstBtnHandle={() => handleSubmission()}
        firstBtnLoading={terminationMutation.isLoading}
        firstBtnDisabled={terminationMutation.isLoading}
        secondBtnLabel="Back"
        secondBtnHandle={() =>
          setPageState(requiresReplacements(impact) ? 'REPLACEMENTS' : 'FILL_DETAILS')
        }
      >
        <Box width={1} display="flex" flexDirection="column" gap={0.5}>
          {(impact?.direct_reports?.length || 0) > 0 && (
            <Typography variant="body2" color="light-gray-text.main">
              {impact.direct_reports.length} direct{' '}
              {impact.direct_reports.length === 1 ? 'report' : 'reports'} will move to{' '}
              {replacements.reportingManager?.name}.
            </Typography>
          )}
          {(impact?.departments_headed || []).map((department) => (
            <Typography key={department.id} variant="body2" color="light-gray-text.main">
              {department.name} will be headed by{' '}
              {replacements.departmentHeads[department.id]?.name}.
            </Typography>
          ))}
          {(impact?.sole_approval_groups || []).map((role) => (
            <Typography key={role.id} variant="body2" color="light-gray-text.main">
              {replacements.approvalRoles[role.id]?.name} will join {role.name}.
            </Typography>
          ))}
          {(impact?.own_unresolved_leave_request_ids?.length || 0) > 0 && (
            <Typography variant="body2" color="light-gray-text.main">
              {impact.own_unresolved_leave_request_ids.length} pending leave{' '}
              {impact.own_unresolved_leave_request_ids.length === 1 ? 'request' : 'requests'} will be
              withdrawn.
            </Typography>
          )}
          {(impact?.approver_unresolved_leave_request_ids?.length || 0) > 0 && (
            <Typography variant="body2" color="light-gray-text.main">
              {impact.approver_unresolved_leave_request_ids.length} unresolved leave{' '}
              {impact.approver_unresolved_leave_request_ids.length === 1 ? 'request' : 'requests'} will
              be rejected.
            </Typography>
          )}
          {requestError && (
            <Typography variant="body2" color="error.main" mt={1}>
              {requestError}
            </Typography>
          )}
        </Box>
      </AdxInfoModal>
    )
  }

  if (pageState === 'REPLACEMENTS') {
    return (
      <AdxFormModal
        open
        disableEscapeKeyDown
        disableAutoFocus
        responsiveScroll
        pageState={pageState}
        icon={<Icon>manage_accounts</Icon>}
        title="Choose replacements"
        handleClose={handleCloseModal}
        onClose={handleCloseModal}
        enableFooter
        cancelText="Back"
        actionText="Continue"
        handleAction={handleReplacementSubmission}
        actionButtonDisabled={replacementStaff.length === 0}
        footerprops={
          <Box display="flex" flexDirection="column" gap={1} p={2}>
            <AdxButton
              variant="contained-destructive"
              onClick={handleReplacementSubmission}
              disabled={replacementStaff.length === 0}
              sx={{ width: 1 }}
            >
              Continue
            </AdxButton>
            <AdxButton variant="ghost" onClick={() => setPageState('FILL_DETAILS')} sx={{ width: 1 }}>
              Back
            </AdxButton>
          </Box>
        }
      >
        {replacementStaff.length === 0 ? (
          <Typography variant="body2" color="error.main">
            No eligible active staff member is available as a replacement.
          </Typography>
        ) : (
          <TerminationReplacementForm
            impact={impact}
            staffOptions={replacementStaff}
            replacements={replacements}
            errors={replacementErrors}
            onReportingManagerChange={(selected) =>
              setReplacements((current) => ({ ...current, reportingManager: selected }))
            }
            onDepartmentHeadChange={(departmentId, selected) =>
              setReplacements((current) => ({
                ...current,
                departmentHeads: { ...current.departmentHeads, [departmentId]: selected },
              }))
            }
            onApprovalRoleChange={(roleId, selected) =>
              setReplacements((current) => ({
                ...current,
                approvalRoles: { ...current.approvalRoles, [roleId]: selected },
              }))
            }
          />
        )}
      </AdxFormModal>
    )
  }

  return (
    <AdxFormModal
      open={true}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      icon={<Icon>person_off</Icon>}
      iconColor="error.main"
      title="Terminate staff"
    >
      <Formik
        enableReinitialize
        initialValues={{
          date: new Date(),
        }}
        validationSchema={theSchema}
        onSubmit={handleDateSubmission}
      >
        {(formikProps, { handleSubmit } = formikProps) => (
          <>
            <Form>
              {pageState === 'FILL_DETAILS' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TerminateEmployeeForm
                    getValidationSchema={getValidationSchema}
                    formikProps={formikProps}
                    staff={staff}
                  />
                  <Box width={1}>
                    <AdxButton
                      sx={{ width: 1 }}
                      variant="contained-destructive"
                      onClick={handleSubmit}
                      isLoading={impactMutation.isLoading}
                      disabled={impactMutation.isLoading}
                    >
                      Terminate
                    </AdxButton>
                    {requestError && (
                      <Typography variant="body2" color="error.main" mt={1}>
                        {requestError}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Form>
          </>
        )}
      </Formik>
    </AdxFormModal>
  )
}

export default TerminateStaff
